import * as React from 'react';
import {
	Box, Button, ButtonGroup, CircularProgress, ClickAwayListener,
	Grow, MenuItem, MenuList, Paper, Popper, Typography,
} from '@mui/material';
import { Download, ArrowDropDown } from '@mui/icons-material';

interface BranchInfo {
	name: string;
	isDefault: boolean;
}

interface VersionInfo {
	filename: string;
	version: string;
	hash: string;
	url: string;
	size: number;
	createdAt: string;
}

function fmtBytes(bytes: number): string {
	if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
	if (bytes >= 1_048_576)     return `${(bytes / 1_048_576).toFixed(1)} MB`;
	if (bytes >= 1_024)         return `${(bytes / 1_024).toFixed(1)} KB`;
	return `${bytes} B`;
}

export default function DownloadButton() {
	const [open, setOpen]             = React.useState(false);
	const [loading, setLoading]       = React.useState(true);
	const [latest, setLatest]         = React.useState<VersionInfo | null>(null);
	const [defaultBranch, setDefault] = React.useState<string>('');
	const [others, setOthers]         = React.useState<{ branch: BranchInfo; latest: VersionInfo | null }[]>([]);
	const anchorRef                   = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		(async () => {
			try {
				const branchRes = await fetch('/api/branches');
				if (!branchRes.ok) return;
				const branches: BranchInfo[] = await branchRes.json();

				const def = branches.find(b => b.isDefault) ?? branches[0];
				if (!def) return;
				setDefault(def.name);

				const latRes = await fetch(`/api/branches/${encodeURIComponent(def.name)}/latest`);
				if (latRes.ok) setLatest(await latRes.json());

				const otherBranches = branches.filter(b => b.name !== def.name);
				const withLatest = await Promise.all(
					otherBranches.map(async b => {
						const r = await fetch(`/api/branches/${encodeURIComponent(b.name)}/latest`);
						return { branch: b, latest: r.ok ? (await r.json() as VersionInfo) : null };
					})
				);
				setOthers(withLatest);
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	if (loading) {
		return <CircularProgress size={36} color='primary' />;
	}

	if (!latest) {
		return (
			<Typography variant='body2' color='text.secondary'>
				No releases available yet. Check back soon.
			</Typography>
		);
	}

	return (
		<>
			<ButtonGroup variant='contained' ref={anchorRef}>
				<Button
					size='large'
					endIcon={<Download />}
					onClick={() => window.open(latest.url, '_blank')}
				>
					Download for Windows
				</Button>
				{others.length > 0 && (
					<Button size='large' sx={{ px: 0.5 }} onClick={() => setOpen(p => !p)}>
						<ArrowDropDown />
					</Button>
				)}
			</ButtonGroup>

			<Popper
				sx={{ zIndex: 1 }}
				open={open}
				anchorEl={anchorRef.current}
				transition
				disablePortal
				placement='bottom-end'
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
					>
						<Paper>
							<ClickAwayListener onClickAway={() => setOpen(false)}>
								<MenuList sx={{ minWidth: 260 }}>
									{others.map(({ branch, latest: v }) => (
										<MenuItem
											key={branch.name}
											disabled={!v}
											onClick={() => { if (v) { window.open(v.url, '_blank'); setOpen(false); } }}
										>
											<Box sx={{ flex: 1 }}>
												<Typography variant='body2' sx={{ fontWeight: 'medium' }}>
													{branch.name}
												</Typography>
												{v && (
													<Typography variant='caption' color='text.secondary'>
														v{v.version} &bull; {fmtBytes(v.size)}
													</Typography>
												)}
											</Box>
											<Download sx={{ ml: 1 }} fontSize='small' />
										</MenuItem>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>

			<Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
				{defaultBranch && <><strong>{defaultBranch}</strong> &bull; </>}
				v{latest.version} &bull; {fmtBytes(latest.size)} &bull; Windows 10/11 64-bit
			</Typography>
		</>
	);
}
